import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Cloud, TrendingUp, Workflow, Lock } from 'lucide-react';
import BookAssessment from './BookAssessment';
import { CTA_LABEL_AWS } from '../site';

// Define and export the Service type
export interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
  // A service has one or the other: a page of its own, or a modal. AFK Development
  // took the page, so its long copy has a URL to be linked, cited and crawled.
  href?: string;
  longDescription?: string;
  imageUrl?: string;
  link: string;
  category: 'ai' | 'cloud';
}

const servicesData: Service[] = [
  // AI Engineering (Primary)
  {
    icon: Workflow,
    title: "AI Agents & Agentic Workflows",
    description: "We design and build agents that use your tools, read your documents, and finish multi-step work. Not a chatbot that answers questions.",
    longDescription:
      '<p>Document question-answering, data analysis, report assembly, multi-step research, routine correspondence. Work that needs judgment at each step, not a single answer. We build the agent, prove it on your real cases before it touches production, and keep it honest once it is live.</p>' +
      '<p>Built end to end, specializing in Anthropic Claude, led by a Claude Certified Architect. Cloud-agnostic: agents run where your systems already are.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">What we build</h3>' +
      '<ul>' +
      '<li><strong>Tool use:</strong> Agents that call your systems and APIs through MCP servers built for the task, so they act rather than advise.</li>' +
      '<li><strong>Retrieval:</strong> Retrieval-augmented reasoning over your own documents and data, so answers are grounded in your material.</li>' +
      '<li><strong>Memory and orchestration:</strong> Multi-step and multi-agent workflows with the memory, context and conditional logic to carry work forward.</li>' +
      '<li><strong>Evaluation and monitoring:</strong> Tested against your real cases before launch, with logging and alerting to catch drift after.</li>' +
      '</ul>' +
      '<p>Delegation and supervision, not autocomplete: you define the goals and constraints, the workflow does the work.</p>',
    link: "#service-ai-workflow-engineering",
    category: 'ai'
  },
  {
    icon: Bot,
    title: "AFK Development",
    description: "Away-from-keyboard development: your team writes the spec and reviews the pull request, and agents implement everything in between. The pipeline we ship with, and install for yours.",
    href: "/afk-development",
    link: "#service-afk-development",
    category: 'ai'
  },
  // AWS Cloud Services (Secondary but important)
  {
    icon: Cloud,
    title: "Cloud Architecture & DevOps",
    description: "Complete AWS infrastructure design, implementation, and automation with CI/CD pipelines and Infrastructure as Code.",
    longDescription:
      '<p>Build scalable, secure, and cost-effective AWS infrastructure with our comprehensive cloud architecture and DevOps services. We handle everything from design to deployment.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Complete Cloud Solutions</h3>' +
      '<ul>' +
      '<li><strong>Infrastructure Design:</strong> Scalable AWS architectures following best practices and Well-Architected Framework.</li>' +
      '<li><strong>DevOps Automation:</strong> CI/CD pipelines, Infrastructure as Code, and automated deployment strategies.</li>' +
      '<li><strong>Migration Services:</strong> Seamless migration from on-premises or other cloud providers to AWS.</li>' +
      '<li><strong>Performance Optimization:</strong> Continuous monitoring and optimization for peak performance.</li>' +
      '</ul>',
    link: "#service-cloud-devops",
    category: 'cloud'
  },
  {
    icon: Lock,
    title: "Security & Monitoring",
    description: "Comprehensive AWS security implementation and 24/7 monitoring solutions to protect your infrastructure and ensure optimal performance.",
    longDescription:
      '<p>Secure your AWS environment with enterprise-grade security measures and continuous monitoring. We implement layered security and provide round-the-clock oversight.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Security & Monitoring Services</h3>' +
      '<ul>' +
      '<li><strong>Security Implementation:</strong> Multi-layered security architecture, compliance frameworks, and threat protection.</li>' +
      '<li><strong>24/7 Monitoring:</strong> Real-time monitoring, alerting, and incident response for critical infrastructure.</li>' +
      '<li><strong>Compliance Management:</strong> Ensure adherence to industry standards and regulatory requirements.</li>' +
      '<li><strong>Automated Recovery:</strong> Self-healing systems and automated disaster recovery procedures.</li>' +
      '</ul>',
    link: "#service-security-monitoring",
    category: 'cloud'
  },
  {
    icon: TrendingUp,
    title: "Cost Optimization & Support",
    description: "Ongoing AWS cost management, performance optimization, and comprehensive support to maximize your cloud investment ROI.",
    longDescription:
      '<p>Maximize your AWS investment with intelligent cost optimization and ongoing support. We ensure you\'re getting the best value from your cloud infrastructure.</p>' +
      '<h3 class="text-xl font-semibold text-gray-100 mt-4 mb-3">Optimization & Support</h3>' +
      '<ul>' +
      '<li><strong>Cost Analysis:</strong> Detailed AWS spend analysis and identification of cost-saving opportunities.</li>' +
      '<li><strong>Resource Optimization:</strong> Right-sizing, reserved instances, and automated scaling for optimal costs.</li>' +
      '<li><strong>Ongoing Support:</strong> Dedicated support team for maintenance, updates, and continuous improvement.</li>' +
      '<li><strong>Performance Tuning:</strong> Regular performance reviews and optimization recommendations.</li>' +
      '</ul>',
    link: "#service-cost-support",
    category: 'cloud'
  }
];

// Export the services array
export { servicesData as services };

interface ServicesSectionProps {
  onOpenServiceModal: (service: Service) => void;
}

interface ServiceEntryProps {
  service: Service;
  onOpenServiceModal: (service: Service) => void;
}

const PRIMARY_ACTION_CLASS =
  'mt-2 inline-flex min-h-[44px] cursor-pointer items-center border-none bg-transparent px-0 py-2.5 text-left font-medium text-accent-300 transition-colors duration-150 hover:text-accent-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400';

// Declared at module scope on purpose: defined inside the parent, these would get a
// new identity on every render, remounting the cards and detaching the button the
// modal restores focus to when it closes.
//
// The primary practice gets scale; the secondary one gets density. The size
// difference is the hierarchy, so neither needs a label explaining its rank.
const PrimaryService: React.FC<ServiceEntryProps> = ({ service, onOpenServiceModal }) => {
  const IconComponent = service.icon;
  const action = (
    <>
      How this works <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
    </>
  );
  return (
    <article className="border-t border-gray-700 pt-8 md:grid md:grid-cols-12 md:gap-10">
      <div className="md:col-span-5">
        <h3 className="flex items-start font-display text-display-sm font-medium text-white">
          <IconComponent className="mr-3 mt-1 h-5 w-5 flex-shrink-0 text-accent-400" aria-hidden="true" />
          {service.title}
        </h3>
      </div>
      <div className="mt-4 md:col-span-7 md:mt-0">
        <p className="max-w-measure text-lg text-gray-300">{service.description}</p>
        {service.href ? (
          <Link to={service.href} className={PRIMARY_ACTION_CLASS}>
            {action}
          </Link>
        ) : (
          <button onClick={() => onOpenServiceModal(service)} className={PRIMARY_ACTION_CLASS}>
            {action}
          </button>
        )}
      </div>
    </article>
  );
};

const SecondaryService: React.FC<ServiceEntryProps> = ({ service, onOpenServiceModal }) => (
  <div className="border-t border-gray-700 pt-5">
    <h3 className="font-display text-base font-medium text-white">{service.title}</h3>
    <p className="mt-2 text-sm text-gray-400">{service.description}</p>
    <button
      onClick={() => onOpenServiceModal(service)}
      className="mt-1 inline-flex min-h-[44px] cursor-pointer items-center border-none bg-transparent px-0 py-2 text-left text-sm font-medium text-cloud-300 transition-colors duration-150 hover:text-cloud-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
    >
      Details <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
    </button>
  </div>
);

const ServicesSection: React.FC<ServicesSectionProps> = ({ onOpenServiceModal }) => {
  const aiServices = servicesData.filter(service => service.category === 'ai');
  const cloudServices = servicesData.filter(service => service.category === 'cloud');

  return (
    <div id="services" className="scroll-mt-20 bg-gray-800 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-measure">
          <h2 className="font-display text-display-md font-bold text-white sm:text-display-lg">
            What we do
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            We build the agents. We install the pipeline that builds them. And we keep the cloud underneath solid.
          </p>
        </div>

        <div className="mt-14 space-y-10">
          {aiServices.map((service) => (
            <PrimaryService key={service.title} service={service} onOpenServiceModal={onOpenServiceModal} />
          ))}
        </div>

        <div id="aws" className="mt-20 scroll-mt-20">
          <p className="font-mono text-eyebrow uppercase text-cloud-400">AWS Cloud Infrastructure</p>
          <div className="mt-6 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {cloudServices.map((service) => (
              <SecondaryService key={service.title} service={service} onOpenServiceModal={onOpenServiceModal} />
            ))}
          </div>
          {/* The second practice keeps its own free assessment. It lives here rather than
              in the hero so the page never asks a first-time visitor to pick a practice. */}
          <p className="mt-10 max-w-measure text-gray-400">
            Ten years of production AWS, and a free infrastructure assessment to go with it: where
            the spend actually goes, what is exposed, and what we would change first.
          </p>
          <BookAssessment
            placement="aws"
            variant="inline"
            tone="cloud"
            band="gray-800"
            label={CTA_LABEL_AWS}
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;